/**
 * Register to Win links — which event each "Register To Win - <event>" subsource belongs to.
 *
 * The pipeline auto-matches each RTW subsource to an event by name (case and punctuation
 * ignored). Ones it can't place (spelling variants, events with no regular leads) are listed
 * first so an editor can link them to an event, or mark them "Register to Win only".
 *
 * Choices are staged on the page and saved together with "Submit matches" (one all-or-nothing
 * request). Links are applied when the report is read (vw_event_rtw), so once submitted the
 * Appointment Updates report uses them on its next load, with no pipeline run. Picking
 * "Automatic match" on a linked row removes its link.
 */
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { EventsNav } from "../../events/EventsNav";
import { fetchRtwLinks, submitRtwLinks } from "../../lib/eventReporting";
import type { RtwLink, RtwLinkChange, RtwLinks } from "../../lib/eventReporting";

const RTW_ONLY = "__rtw_only__";
const AUTO = "__auto__";
const selectCls = "h-8 px-2 rounded-md border hairline text-[13px] focus-ring w-full min-w-[13rem] max-w-[22rem]";
const selectStyle = { background: "var(--bg)", color: "var(--ink)" };

const STATUS: Record<RtwLink["match_status"], { label: string; fg: string; bg: string }> = {
  unmatched: { label: "Needs an event", fg: "var(--oxblood)", bg: "var(--oxblood-bg)" },
  auto: { label: "Automatic", fg: "var(--ink-3)", bg: "var(--bg)" },
  linked: { label: "Linked", fg: "var(--forest)", bg: "var(--forest-bg)" },
  rtw_only: { label: "Register to Win only", fg: "var(--forest)", bg: "var(--forest-bg)" },
};

/** The row's saved choice as a select value. */
function savedValue(r: RtwLink): string {
  return r.match_status === "rtw_only" ? RTW_ONLY : r.match_status === "linked" ? r.parent_subsource
    : r.match_status === "auto" ? AUTO : "";
}

/** "Automatic match" only places the row if its auto parent is an event with regular leads. */
function autoLabel(r: RtwLink, events: string[]): string {
  return events.includes(r.auto_parent) ? `Automatic match (${r.auto_parent})` : "Remove link (needs an event)";
}

function choiceLabel(r: RtwLink, v: string, events: string[]): string {
  return v === RTW_ONLY ? "Register to Win only" : v === AUTO ? autoLabel(r, events).toLowerCase() : v;
}

function dateLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${+m}/${+d}/${y}`;
}

export function EventRtwLinks() {
  const { user } = useAuth();
  const canEdit = !!user?.has_event_editor;
  const [data, setData] = useState<RtwLinks | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMatched, setShowMatched] = useState(false);
  // Staged choices, keyed by RTW subsource; nothing is saved until "Submit matches".
  const [pending, setPending] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    fetchRtwLinks().then(setData).catch((e: Error) => setError(e.message));
  }, []);

  const pendingCount = Object.keys(pending).length;
  // Warn before closing the tab with unsubmitted matches.
  useEffect(() => {
    if (pendingCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pendingCount]);

  const needs = useMemo(() => (data?.rows ?? []).filter((r) => r.match_status === "unmatched"), [data]);
  // Rows with a staged change stay visible even when "Show matched" is off.
  const shown = (data?.rows ?? []).filter((r) => showMatched || r.match_status === "unmatched" || r.rtw_subsource in pending);

  function stage(r: RtwLink, v: string) {
    setMessage(null);
    setPending((p) => {
      const next = { ...p };
      if (v === "" || v === savedValue(r)) delete next[r.rtw_subsource];
      else next[r.rtw_subsource] = v;
      return next;
    });
  }

  async function submit() {
    if (!data || pendingCount === 0) return;
    setSubmitting(true);
    setMessage(null);
    const changes: RtwLinkChange[] = Object.entries(pending).map(([rtw_subsource, v]) => ({
      rtw_subsource,
      parent_subsource: v === RTW_ONLY || v === AUTO ? null : v,
      use_auto: v === AUTO,
    }));
    try {
      setData(await submitRtwLinks(changes));
      setPending({});
      setMessage({
        ok: true,
        text: `Submitted ${changes.length} ${changes.length === 1 ? "match" : "matches"}. The Appointment Updates report now uses them.`,
      });
    } catch (e) {
      setMessage({ ok: false, text: `Nothing was saved: ${(e as Error).message}` });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4">
        <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>Register to Win</h1>
        <p className="text-[14.5px] font-serif italic mt-1" style={{ color: "var(--ink-3)" }}>
          Which event each Register to Win subsource belongs to, for the Register to Win tiles.
        </p>
      </header>

      <EventsNav />

      {error && (
        <div className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Failed to load Register to Win: {error}</span>
        </div>
      )}

      <p className="text-[13px] mb-4 max-w-6xl" style={{ color: "var(--ink-3)" }}>
        Each refresh matches "Register To Win - &lt;event&gt;" to the event of the same name. The ones listed here
        didn't match: the name is spelled differently in LeadPerfection, or the event has no regular leads.
        {canEdit
          ? " Pick the event they belong to, or \"Register to Win only\" if there isn't one, then Submit matches to update the report."
          : " An event editor can link them to an event."}
      </p>

      {message && (
        <div className="mb-3 text-[13px] max-w-6xl flex flex-wrap items-center gap-x-3 gap-y-1"
          style={{ color: message.ok ? "var(--forest)" : "var(--oxblood)" }}>
          {message.ok && <Check size={14} />}
          <span>{message.text}</span>
          {message.ok && (
            <Link to="/events/appointments" className="inline-flex items-center gap-1 underline underline-offset-2 focus-ring">
              Open Appointment Updates <ArrowRight size={13} />
            </Link>
          )}
        </div>
      )}

      {canEdit && data && (
        <div className="sticky top-0 z-10 mb-3 max-w-6xl rounded-lg border hairline px-4 py-2.5 flex flex-wrap items-center gap-3"
          style={{ background: "var(--bg-elev)" }}>
          <span className="text-[13px]" style={{ color: pendingCount ? "var(--ink)" : "var(--ink-3)" }}>
            {pendingCount ? `${pendingCount} ${pendingCount === 1 ? "change" : "changes"} not submitted yet` : "No changes to submit"}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {pendingCount > 0 && (
              <button type="button" onClick={() => { setPending({}); setMessage(null); }} disabled={submitting}
                className="px-3 py-1.5 rounded-md text-[13px] border hairline t-base focus-ring disabled:opacity-50"
                style={{ color: "var(--ink-2)" }}>
                Discard
              </button>
            )}
            <button type="button" onClick={submit} disabled={submitting || pendingCount === 0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-40"
              style={{ background: "var(--ink)", color: "var(--bg)" }}>
              <Check size={14} strokeWidth={2} />
              {submitting ? "Submitting…" : "Submit matches"}
            </button>
          </div>
        </div>
      )}

      <section className="rounded-lg border hairline overflow-hidden max-w-6xl" style={{ background: "var(--bg-elev)" }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <span className="font-serif text-[14px]" style={{ color: "var(--ink)" }}>
            {data ? (needs.length ? `${needs.length} need an event` : "Every Register to Win subsource has an event") : ""}
          </span>
          <label className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: "var(--ink-3)" }}>
            <input type="checkbox" checked={showMatched} onChange={(e) => setShowMatched(e.target.checked)} />
            Show matched ({(data?.rows.length ?? 0) - needs.length})
          </label>
        </div>
        {!data && !error ? (
          <div className="p-5 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>Loading…</div>
        ) : shown.length === 0 ? (
          <div className="px-5 pb-5 text-[13px]" style={{ color: "var(--ink-3)" }}>Nothing to match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline" style={{ color: "var(--ink-3)" }}>
                  <th className="px-5 py-2 font-normal">Register to Win subsource</th>
                  <th className="px-3 py-2 font-normal text-right">Registrations</th>
                  <th className="px-3 py-2 font-normal">Entered</th>
                  <th className="px-5 py-2 font-normal">Event</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const st = STATUS[r.match_status];
                  const staged = pending[r.rtw_subsource];
                  const value = staged ?? savedValue(r);
                  return (
                    <tr key={r.rtw_subsource} className="border-b hairline last:border-0"
                      style={{ color: "var(--ink)", background: staged ? "var(--forest-bg)" : undefined }}>
                      <td className="px-5 py-2 min-w-[13rem]">
                        <div>{r.rtw_subsource}</div>
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border hairline"
                          style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                        {staged && (
                          <span className="ml-2 text-[11.5px] italic" style={{ color: "var(--forest)" }}>
                            → {choiceLabel(r, staged, data!.events)} (not submitted)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.registrations.toLocaleString("en-US")}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">
                        {dateLabel(r.first_entry)}{r.last_entry !== r.first_entry ? ` – ${dateLabel(r.last_entry)}` : ""}
                      </td>
                      <td className="px-5 py-2">
                        {canEdit ? (
                          <select value={value} disabled={submitting}
                            aria-label={`Event for ${r.rtw_subsource}`}
                            onChange={(e) => stage(r, e.target.value)}
                            className={selectCls} style={selectStyle}>
                            {r.match_status === "unmatched" && <option value="">Choose an event…</option>}
                            {r.match_status !== "unmatched" && (
                              <option value={AUTO}>{autoLabel(r, data!.events)}</option>
                            )}
                            <option value={RTW_ONLY}>Register to Win only (no event leads)</option>
                            {data!.events.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                          </select>
                        ) : (
                          <span>{r.match_status === "unmatched" ? "–" : r.match_status === "rtw_only" ? "Register to Win only" : r.parent_subsource}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
